import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Link, Location, useLocation, useNavigate } from "react-router-dom";
import { LocalRoutesNavTargets } from "@/LocalRoutes";
import { useAuth } from "@/hooks/use-auth";
import { FormikInput } from "./formik-input";
import { FormikPasswordInput } from "./formik-password-input";

export const LoginForm = ({
    className,
    ...props
}: ComponentPropsWithoutRef<"div">) => {
    const { t } = useTranslation();
    const { loginMutation } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as { from?: Location } | null)?.from;

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader>
                    <CardTitle className="text-2xl">
                        {t("components.login-form.title")}
                    </CardTitle>
                    <CardDescription>
                        {t("components.login-form.description")}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Formik
                        initialValues={{
                            email: "",
                            password: "",
                        }}
                        onSubmit={(values) => {
                            loginMutation.mutate(
                                {
                                    email: values.email,
                                    password: values.password,
                                },
                                {
                                    onSuccess: (r) => {
                                        if (r.status === 200) {
                                            const target =
                                                from &&
                                                from.pathname !==
                                                    LocalRoutesNavTargets.LOGIN
                                                    ? `${from.pathname}${from.search}${from.hash}`
                                                    : LocalRoutesNavTargets.DASHBOARD;
                                            void navigate(target, {
                                                replace: true,
                                            });
                                        }
                                    },
                                },
                            );
                        }}
                        validationSchema={Yup.object({
                            email: Yup.string()
                                .email(
                                    t(
                                        "components.login-form.label-email-valid",
                                    ),
                                )
                                .required(
                                    t(
                                        "components.login-form.label-email-required",
                                    ),
                                ),
                            password: Yup.string().required(
                                t(
                                    "components.login-form.label-password-required",
                                ),
                            ),
                        })}
                    >
                        {({ isValid, dirty, errors }) => (
                            <Form>
                                <div className="flex flex-col gap-6">
                                    <div className="grid gap-2">
                                        <Label htmlFor="email">
                                            {t(
                                                "components.login-form.label-email",
                                            )}
                                        </Label>
                                        <FormikInput
                                            autoComplete="email"
                                            id="email"
                                            name="email"
                                            placeholder="max@example.com"
                                            tabIndex={1}
                                            type="email"
                                        />
                                        {errors.email ? (
                                            <span className="text-xs text-red-600">
                                                {errors.email}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="grid gap-2">
                                        <div className="flex items-center">
                                            <Label htmlFor="password">
                                                {t(
                                                    "components.login-form.label-password",
                                                )}
                                            </Label>
                                            <Link
                                                className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                                                tabIndex={4}
                                                to={
                                                    LocalRoutesNavTargets.PASSWORD_RESET
                                                }
                                            >
                                                {t(
                                                    "components.login-form.label-forgot-password",
                                                )}
                                            </Link>
                                        </div>
                                        <FormikPasswordInput
                                            autoComplete="current-password"
                                            id="password"
                                            name="password"
                                            tabIndex={2}
                                        />
                                        {errors.password ? (
                                            <span className="text-xs text-red-600">
                                                {errors.password}
                                            </span>
                                        ) : null}
                                    </div>
                                    {loginMutation.isError ? (
                                        <span className="text-xs text-red-600">
                                            {t(
                                                "components.login-form.label-login-failed",
                                            )}
                                        </span>
                                    ) : null}
                                    <Button
                                        className="w-full"
                                        disabled={!isValid || !dirty}
                                        tabIndex={3}
                                        type="submit"
                                    >
                                        {t(
                                            "components.login-form.action-login",
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </div>
    );
};
